import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default Leaflet marker icons (CRA can't resolve image paths otherwise)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom school icon (black square with yellow S)
const schoolIcon = L.divIcon({
  className: "",
  html: `<div style="width:28px;height:28px;background:#0A0A0B;color:#FFC60A;border:2px solid #0A0A0B;display:flex;align-items:center;justify-content:center;font-family:'IBM Plex Mono',monospace;font-weight:800;font-size:12px;">S</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const stopIcon = (n) => L.divIcon({
  className: "",
  html: `<div style="width:24px;height:24px;background:#ffffff;color:#0A0A0B;border:2px solid #0A0A0B;display:flex;align-items:center;justify-content:center;font-family:'IBM Plex Mono',monospace;font-weight:700;font-size:11px;">${n}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// ─────────────────────────────────────────────────────────────────────────────
// Bus marker — modern card with bus icon, route label, status color, heading arrow
// ─────────────────────────────────────────────────────────────────────────────
const BUS_COLORS = {
  ON_TIME:    { bg: "#10B981", text: "#FFFFFF", label: "À l'heure" },     // emerald
  LATE_1_MIN: { bg: "#FACC15", text: "#0A0A0B", label: "Retard léger" },  // yellow
  LATE_3_MIN: { bg: "#F97316", text: "#FFFFFF", label: "Retard important" }, // orange
  CRITICAL:   { bg: "#EF4444", text: "#FFFFFF", label: "Retard critique" }, // red
  AHEAD:      { bg: "#3B82F6", text: "#FFFFFF", label: "En avance" },
  DELAYED:    { bg: "#F97316", text: "#FFFFFF", label: "Retard" },
};

const busIcon = (status, label, { isStale = false, heading = null, selected = false } = {}) => {
  const c = BUS_COLORS[status] || { bg: "#0A0A0B", text: "#FFC60A", label: "—" };
  const opacity = isStale ? 0.5 : 1;
  const ring = selected ? "box-shadow:0 0 0 3px #FFC60A, 3px 3px 0 0 rgba(0,0,0,0.4);" : "box-shadow:2px 2px 0 0 rgba(0,0,0,0.35);";
  const headingArrow = (typeof heading === "number" && !isStale) ? `
    <div style="position:absolute;top:-7px;left:50%;transform:translateX(-50%) rotate(${heading}deg);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:8px solid ${c.bg};filter:drop-shadow(0 1px 0 #0A0A0B);"></div>` : "";
  const pulse = !isStale ? `<div style="position:absolute;inset:-4px;border-radius:18px;background:${c.bg};opacity:0.35;animation:lpulse 1.6s ease-in-out infinite;"></div>` : "";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;">
      ${pulse}
      ${headingArrow}
      <div style="position:relative;display:inline-flex;align-items:center;gap:5px;padding:4px 9px 4px 6px;background:${c.bg};color:${c.text};border:2px solid #0A0A0B;border-radius:14px;font-family:'IBM Plex Mono',monospace;font-weight:800;font-size:11px;line-height:1;${ring}opacity:${opacity};white-space:nowrap;cursor:pointer;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"/><path d="M16 6v6"/><path d="M2 12h19a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-1a3 3 0 1 1-6 0H9a3 3 0 1 1-6 0H2z"/><path d="M2 12v-3a3 3 0 0 1 3-3h13a3 3 0 0 1 3 3v3"/></svg>
        <span>${label || "BUS"}</span>
      </div>
    </div>
    <style>@keyframes lpulse { 0%,100% { transform: scale(1); opacity: 0.35; } 50% { transform: scale(1.35); opacity: 0; } }</style>`,
    iconSize: [60, 26],
    iconAnchor: [30, 13],
  });
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [28, 28], maxZoom: 15 });
    }
  }, [map, JSON.stringify(points)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * CenterOn — pans/zooms to a specific location whenever the `target` ref
 * changes. Used by the Mission Control Center "Localiser" button to focus
 * on the selected mission's bus.
 *   target = { lat, lng, zoom?: number } | null
 */
function CenterOn({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target || typeof target.lat !== "number" || typeof target.lng !== "number") return;
    map.flyTo([target.lat, target.lng], target.zoom || 17, { duration: 0.7 });
  }, [map, target?.lat, target?.lng, target?.zoom, target?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function LeafletMap({
  waypoints = [],
  vehicles = null,
  onVehicleClick,
  selectedVehicleId = null,
  showLegend = true,
  height = 320,
  showPath = true,
  testId = "leaflet-map",
  center = [46.5083, 6.4977],   // Morges, VD (CH) — default for Logibus
  zoom = 13,
  shapeCoords = null,
  historyCoords = null,
  staleThresholdSeconds = 300,
  emptyMessage = null,
  centerOn = null,
}) {
  const allPoints = useMemo(() => {
    const pts = (waypoints || []).filter((p) => p && typeof p.lat === "number" && typeof p.lng === "number");
    if (Array.isArray(vehicles)) vehicles.forEach((v) => {
      if (typeof v?.lat === "number" && typeof v?.lon === "number") pts.push({ lat: v.lat, lng: v.lon });
    });
    if (Array.isArray(shapeCoords)) shapeCoords.forEach(([la, ln]) => pts.push({ lat: la, lng: ln }));
    return pts;
  }, [waypoints, vehicles, shapeCoords]);

  const pathCoords = (waypoints || []).filter(Boolean).map((p) => [p.lat, p.lng]);

  // Live counter
  const counters = useMemo(() => {
    const list = Array.isArray(vehicles) ? vehicles : [];
    const active = list.length;
    const critical = list.filter((v) => v.status === "CRITICAL").length;
    const late = list.filter((v) => v.status === "LATE_3_MIN" || v.status === "LATE_1_MIN").length;
    return { active, critical, late };
  }, [vehicles]);

  return (
    <div data-testid={testId} className="border border-zinc-900 overflow-hidden relative" style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />

        {Array.isArray(shapeCoords) && shapeCoords.length > 1 && (
          <Polyline positions={shapeCoords} pathOptions={{ color: "#2563EB", weight: 5, opacity: 0.85 }} />
        )}

        {showPath && pathCoords.length > 1 && !(Array.isArray(shapeCoords) && shapeCoords.length > 1) && (
          <Polyline positions={pathCoords} pathOptions={{ color: "#0A0A0B", weight: 3, dashArray: "6 6" }} />
        )}

        {Array.isArray(historyCoords) && historyCoords.length > 1 && (
          <Polyline positions={historyCoords} pathOptions={{ color: "#22C55E", weight: 3, opacity: 0.8 }} />
        )}

        {(waypoints || []).map((p, i) => (
          <Marker key={`wp-${i}`} position={[p.lat, p.lng]} icon={i === 0 ? schoolIcon : stopIcon(i)}>
            {p.address && <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>{p.address}</Tooltip>}
          </Marker>
        ))}

        {Array.isArray(vehicles) && vehicles.map((v) => {
          if (typeof v?.lat !== "number" || typeof v?.lon !== "number") return null;
          const label = v.route_short_name || v.label || v.plate || (v.navixy_tracker_id ? `#${v.navixy_tracker_id}` : "BUS");
          const lastSeenAgo = v.last_seen ? (Date.now() - new Date(v.last_seen).getTime()) / 1000 : 0;
          const isStale = lastSeenAgo > staleThresholdSeconds;
          const selected = selectedVehicleId && (v.vehicle_id === selectedVehicleId || v.navixy_tracker_id === selectedVehicleId);
          const color = BUS_COLORS[v.status] || BUS_COLORS.ON_TIME;
          const lastSeenLabel = v.last_seen ? new Date(v.last_seen).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
          return (
            <Marker
              key={`bus-${v.navixy_tracker_id || v.id || v.vehicle_id}`}
              position={[v.lat, v.lon]}
              icon={busIcon(v.status, label, { isStale, heading: v.heading, selected })}
              eventHandlers={onVehicleClick ? { click: () => onVehicleClick(v) } : undefined}
            >
              <Tooltip direction="top" offset={[0, -16]} opacity={0.95}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                  <strong>Bus {label}</strong> · {color.label}
                </div>
              </Tooltip>
              <Popup>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ background: color.bg, color: color.text, padding: "2px 6px", borderRadius: 8, fontWeight: 800 }}>🚌 {label}</span>
                    <span style={{ color: "#666" }}>{color.label}</span>
                  </div>
                  <div><b>Bus :</b> {v.plate || v.label || v.vehicle_id}</div>
                  <div><b>Ligne :</b> {v.route_short_name || "—"} {v.headsign ? `→ ${v.headsign}` : ""}</div>
                  <div><b>Chauffeur :</b> {v.driver_name || "—"}</div>
                  <div>
                    <b>Traceur GPS :</b>{" "}
                    {v.gps_source === "real"
                      ? <span style={{ color: "#15803d" }}>RÉEL · {v.gps_tracker_display || v.tracker_label || "Traceur GPS lié"}</span>
                      : v.is_simulated
                        ? <span style={{ color: "#c2410c" }}>Simulé (démo)</span>
                        : <span style={{ color: "#71717a" }}>Aucun</span>}
                  </div>
                  <div><b>Vitesse :</b> {typeof v.speed === "number" ? `${Math.round(v.speed)} km/h` : "—"}</div>
                  <div><b>Retard :</b> {typeof v.delay_minutes === "number" ? `${v.delay_minutes >= 0 ? "+" : ""}${Math.round(v.delay_minutes)} min` : "—"}</div>
                  <div><b>Prochain arrêt :</b> {v.next_stop_name || "—"}</div>
                  <div><b>ETA :</b> {v.scheduled_next_arrival ? String(v.scheduled_next_arrival).slice(0,5) : (typeof v.eta_minutes === "number" ? `${v.eta_minutes} min` : "—")}</div>
                  <div style={{ marginTop: 4, color: isStale ? "#b91c1c" : "#666" }}>
                    <b>Dernier GPS :</b> {lastSeenLabel}{isStale ? " ⚠ obsolète" : ""}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        <FitBounds points={allPoints} />
        <CenterOn target={centerOn} />
      </MapContainer>

      {/* Live counter — top right */}
      {Array.isArray(vehicles) && (
        <div className="absolute top-2 right-2 z-[400] bg-white border border-zinc-900 px-3 py-2 text-[10px] font-mono uppercase tracking-widest shadow-[2px_2px_0_0_#0A0A0B] flex items-center gap-3" data-testid="map-live-counter">
          <span><b className="font-display font-black text-lg tracking-tighter">{counters.active}</b> bus actif{counters.active > 1 ? "s" : ""}</span>
          {counters.late > 0 && <span className="text-orange-600"><b className="font-display font-black text-lg tracking-tighter">{counters.late}</b> retard{counters.late > 1 ? "s" : ""}</span>}
          {counters.critical > 0 && <span className="text-red-600"><b className="font-display font-black text-lg tracking-tighter">{counters.critical}</b> critique{counters.critical > 1 ? "s" : ""}</span>}
        </div>
      )}

      {/* Empty state — when an explicit empty message is provided and no buses */}
      {emptyMessage && Array.isArray(vehicles) && vehicles.length === 0 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[400] bg-white border border-zinc-900 px-4 py-3 text-xs font-mono uppercase tracking-widest text-zinc-700 shadow-[3px_3px_0_0_#0A0A0B]" data-testid="map-empty-message">
          {emptyMessage}
        </div>
      )}

      {/* Legend — bottom right */}
      {showLegend && (
        <div className="absolute bottom-2 right-2 z-[400] bg-white border border-zinc-900 px-3 py-2 text-[10px] font-mono shadow-[2px_2px_0_0_#0A0A0B]" data-testid="map-legend">
          <div className="font-bold uppercase tracking-widest mb-1 text-zinc-500">Légende</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#10B981", border: "1px solid #0A0A0B" }}></span>À l'heure</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#FACC15", border: "1px solid #0A0A0B" }}></span>Léger</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#F97316", border: "1px solid #0A0A0B" }}></span>Important</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#EF4444", border: "1px solid #0A0A0B" }}></span>Critique</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 border border-zinc-900 bg-white text-center text-[8px] font-bold">2</span>Arrêt</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 border border-zinc-900 bg-zinc-900 text-[#FFC60A] text-center text-[8px] font-bold leading-[12px]">S</span>École</span>
          </div>
        </div>
      )}
    </div>
  );
}
